/**
 * Semnătura pe om, din mailurile trimise în numele lui (cereri, follow-up,
 * întrebări, comenzi). Două feluri:
 * - 'card': cartea de vizită făcută de Procumi, din ce știe despre om și firmă;
 * - 'outlook': semnătura pe care o are deja, lipită din Outlook. HTML-ul ei e
 *   curățat pe server înainte să fie salvat, deci aici se folosește ca atare.
 */

export type SignatureMode = 'card' | 'outlook';

export interface UserSignature {
  mode: SignatureMode;
  /** funcția de sub nume, la 'card': „Achiziții”, „Director tehnic” */
  title: string;
  phone: string;
  /** logo-ul firmei în cartea de vizită, dacă firma are unul */
  logo: boolean;
  /** la 'outlook': HTML-ul curățat */
  html: string;
}

export interface SignatureWho {
  name: string;
  email: string;
  company: string;
  /** adresa publică a logo-ului; null = firma n-are logo */
  logoUrl: string | null;
}

export const DEFAULT_SIGNATURE: UserSignature = { mode: 'card', title: '', phone: '', logo: true, html: '' };

const INK = '#0F1D33';
const MUTED = '#59667B';
const BLUE = '#2B3BFF';
const FONT = "Archivo, 'Segoe UI', Arial, Helvetica, sans-serif";

const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

/** Textul unei semnături HTML: un rând pe bloc, fără etichete, cu entitățile citite. */
export function htmlToText(html: string): string {
  return html
    .replace(/<(style|script|head)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6]|table)>/gi, '\n')
    .replace(/<\/td>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (m, e: string) => {
      if (e[0] === '#') {
        const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
        return Number.isFinite(code) ? String.fromCodePoint(code) : m;
      }
      return ENTITIES[e.toLowerCase()] ?? m;
    })
    .split('\n')
    .map((l) => l.replace(/[ \t ]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

const cardLines = (s: UserSignature, who: SignatureWho): string[] =>
  [who.name, s.title, who.company, s.phone, who.email].map((l) => l.trim()).filter(Boolean);

/** Semnătura ca text: în mailurile text și în cele deschise în programul de mail. */
export function signatureText(s: UserSignature, who: SignatureWho): string {
  if (s.mode === 'outlook') {
    const text = htmlToText(s.html);
    if (text) return text;
  }
  return cardLines(s, who).join('\n');
}

/** Semnătura ca HTML, sigură pentru mail: tabele și stiluri pe loc, fără SVG. */
export function signatureHtml(s: UserSignature, who: SignatureWho): string {
  if (s.mode === 'outlook' && s.html.trim()) return s.html;
  const line = (html: string, style: string) => `<div style="${style}">${html}</div>`;
  const small = `margin:2px 0 0;font:400 13px/18px ${FONT};color:${MUTED}`;
  const contacts = [
    s.phone.trim()
      ? `<a href="tel:${esc(s.phone.replace(/[^\d+]/g, ''))}" style="color:${BLUE};text-decoration:none">${esc(s.phone.trim())}</a>`
      : '',
    who.email
      ? `<a href="mailto:${esc(who.email)}" style="color:${BLUE};text-decoration:none">${esc(who.email)}</a>`
      : '',
  ].filter(Boolean);
  const text = [
    line(esc(who.name), `margin:0;font:600 15px/20px ${FONT};color:${INK}`),
    s.title.trim() ? line(esc(s.title.trim()), small) : '',
    who.company ? line(esc(who.company), `margin:2px 0 0;font:600 13px/18px ${FONT};color:${INK}`) : '',
    contacts.length ? line(contacts.join(' &nbsp;·&nbsp; '), `margin:6px 0 0;font:400 13px/18px ${FONT}`) : '',
  ].join('');
  const logo =
    s.logo && who.logoUrl
      ? `<td valign="top" style="padding:2px 14px 0 0"><img src="${esc(who.logoUrl)}" alt="${esc(who.company)}" height="40" style="display:block;height:40px;max-width:120px;border:0"></td>`
      : '';
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 0"><tr>${logo}<td valign="top" style="border-left:2px solid ${BLUE};padding:0 0 0 14px">${text}</td></tr></table>`;
}
